import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle, Clock, CalendarClock, ShieldAlert, Flame, Wrench, CheckCircle2,
  FileWarning, Boxes, ClipboardList, DollarSign, ClipboardCheck, HandCoins, Hourglass, IdCard,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";

const TILES = [
  { key: "overdue",               label: "Overdue",                   tone: "red",   icon: AlertTriangle, testid: "kpi-overdue",             href: "/maintenance?filter=overdue" },
  { key: "due_7",                 label: "Due next 7 days",           tone: "amber", icon: Clock,         testid: "kpi-due-7",               href: "/maintenance?filter=due_7" },
  { key: "due_30",                label: "Due next 30 days",          tone: "amber", icon: CalendarClock, testid: "kpi-due-30",              href: "/maintenance?filter=due_30" },
  { key: "due_90",                label: "Due next 90 days",          tone: "blue",  icon: CalendarClock, testid: "kpi-due-90",              href: "/maintenance?filter=due_90" },
  { key: "regulatory_due",        label: "Regulatory due",            tone: "red",   icon: ShieldAlert,   testid: "kpi-regulatory-due",      href: "/compliance?filter=due_30" },
  { key: "critical_defects_open", label: "Critical defects open",     tone: "red",   icon: Flame,         testid: "kpi-critical-defects",    href: "/defects?risk=Critical" },
  { key: "high_priority",         label: "High-priority tasks",       tone: "amber", icon: Wrench,        testid: "kpi-high-priority",       href: "/maintenance?filter=high_priority" },
  { key: "awaiting_contractor",   label: "Awaiting contractor",       tone: "amber", icon: Hourglass,     testid: "kpi-awaiting-contractor", href: "/work-orders?filter=awaiting_contractor" },
  { key: "awaiting_approval",     label: "Awaiting approval",         tone: "amber", icon: ClipboardCheck,testid: "kpi-awaiting-approval",   href: "/work-orders?filter=awaiting_approval" },
  { key: "completed_this_month",  label: "Completed this month",      tone: "green", icon: CheckCircle2,  testid: "kpi-completed-month",     href: "/work-orders?filter=completed_month" },
  { key: "expiring_certificates", label: "Expiring certificates (90d)", tone: "amber", icon: FileWarning, testid: "kpi-expiring-certificates", href: "/compliance?filter=expiring" },
  { key: "supplier_expiring",     label: "Supplier licence / insurance expiring (30d)", tone: "amber", icon: IdCard, testid: "kpi-supplier-expiring", href: "/suppliers" },
  { key: "assets_without_inspection", label: "Assets without inspection", tone: "grey", icon: Boxes,     testid: "kpi-assets-no-inspection", href: "/assets" },
  { key: "open_work_orders",      label: "Open work orders",          tone: "blue",  icon: ClipboardList, testid: "kpi-open-work-orders",    href: "/work-orders?filter=open" },
  { key: "annual_expenditure",    label: "Annual expenditure",        tone: "grey",  icon: DollarSign,    testid: "kpi-annual-expenditure",  href: "/reports",  money: true },
];

const TONE = {
  red:   { text: "text-red-700",   bg: "bg-red-50",    ring: "ring-red-200" },
  amber: { text: "text-amber-700", bg: "bg-amber-50",  ring: "ring-amber-200" },
  green: { text: "text-emerald-700", bg: "bg-emerald-50", ring: "ring-emerald-200" },
  blue:  { text: "text-sky-700",   bg: "bg-sky-50",    ring: "ring-sky-200" },
  grey:  { text: "text-slate-700", bg: "bg-slate-100", ring: "ring-slate-200" },
};

function fmtMoney(n) { return "$" + Number(n || 0).toLocaleString(); }

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => setKpis(r.data)).catch(() => setKpis({}));
    api.get("/dashboard/charts").then((r) => setCharts(r.data)).catch(() => setCharts({}));
  }, []);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Operations</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1">Real-time view of maintenance, compliance and contractor activity across Shady Acres.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TILES.map((t) => {
          const v = kpis?.[t.key];
          const T = TONE[t.tone];
          return (
            <button
              key={t.key}
              onClick={() => nav(t.href)}
              data-testid={t.testid}
              className={`kpi-card text-left rounded-md border border-slate-200 bg-white p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{t.label}</div>
                  <div className={`mt-2 text-3xl font-heading font-bold ${T.text}`}>
                    {kpis === null ? "…" : (t.money ? fmtMoney(v) : (v ?? 0))}
                  </div>
                </div>
                <span className={`p-2 rounded-md ${T.bg} ${T.text}`}><t.icon className="w-4 h-4" /></span>
              </div>
              <div className="mt-3 text-xs text-slate-500">Click to open filtered list →</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-semibold">Maintenance due — next 12 months</h3>
              <Badge variant="outline">from schedule</Badge>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts?.next_12_months || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-heading text-lg font-semibold mb-3">By status</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie dataKey="count" nameKey="status" data={charts?.by_status || []} innerRadius={50} outerRadius={90}>
                    {(charts?.by_status || []).map((entry, i) => (
                      <Cell key={`cell-${entry.status ?? "n"}-${i}`} fill={["#dc2626","#d97706","#16a34a","#0284c7","#64748b"][i % 5]} />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-heading text-lg font-semibold mb-3">Assets by group</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(charts?.by_group || []).slice(0, 15)} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis dataKey="group" type="category" tick={{ fontSize: 11 }} width={200} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0f172a" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
